import { useLayoutEffect } from 'react';

/** Height of a banner that pushes `body` down from outside the React tree. */
export const HOST_BANNER_INSET_VAR = '--host-banner-inset';

const readBodyMarginTop = (): string => {
  const { body } = document;
  // Inline first: it is the target value even mid transition. Computed covers a class-applied margin.
  const marginTop = parseFloat(body.style.marginTop || getComputedStyle(body).marginTop);
  return `${Number.isFinite(marginTop) && marginTop > 0 ? marginTop : 0}px`;
};

/**
 * Publishes `body`'s top margin as `--host-banner-inset` on `:root`, so both layouts can size the
 * shell to `calc(100% - var(--host-banner-inset, 0px))`.
 *
 * Stopgap for Intercom's inline banner: a fixed frame outside the React tree that pushes the page
 * down with a `body` margin and exposes no height. The plan is a banner slot on the ui-library
 * AppShell, surfaced as a prop on PortalShell. A banner rendered there sits in the page flow,
 * so delete this hook and the `calc` once it lands.
 */
export const useHostBannerInset = (): void => {
  useLayoutEffect(() => {
    // On `:root`, not `body` like the host chrome variables: a write to `body` would retrigger the
    // observer below.
    const { style } = document.documentElement;
    const publish = () => style.setProperty(HOST_BANNER_INSET_VAR, readBodyMarginTop());

    publish();
    // `class` too, in case the SDK ever applies the margin via a class instead of inline style.
    const observer = new MutationObserver(publish);
    observer.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
    window.addEventListener('resize', publish);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', publish);
      style.removeProperty(HOST_BANNER_INSET_VAR);
    };
  }, []);
};
